import { useQuery } from '@tanstack/react-query'
import { CalendarDays, Copy, Printer, Settings2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router'
import { errorMessage } from '@/api/errors'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { FormField } from '@/components/forms/FormField'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { classesQuery } from '@/features/academic/api'
import { useAuth } from '@/features/auth/useAuth'
import { sectionTimetableQuery } from '@/features/timetable/api'
import { CellDialog } from '@/features/timetable/components/CellDialog'
import { CopyFromDialog } from '@/features/timetable/components/CopyFromDialog'
import { CoveragePanel } from '@/features/timetable/components/CoveragePanel'
import { WeekGrid } from '@/features/timetable/components/WeekGrid'
import type { Lesson, Period, SectionGrid } from '@/features/timetable/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { sectionLabel } from '@/features/academic/sectionLabel'

interface EditingCell {
  dayOfWeek: number
  period: Period
  lesson?: Lesson
}

/** The grid card + coverage panel for one section. */
export function SectionTimetable({ sectionId, onCopy }: { sectionId: number; onCopy?: () => void }) {
  const { can } = useAuth()
  const canEdit = can('academic.manage')
  const { data, isPending, error, refetch } = useQuery(sectionTimetableQuery(sectionId))
  const [editing, setEditing] = useState<EditingCell | null>(null)

  if (isPending) return <LoadingState />
  if (error) return <ErrorState description={errorMessage(error)} onRetry={() => void refetch()} />
  return (
    <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
      <GridCard grid={data} canEdit={canEdit} onCopy={onCopy} onEdit={setEditing} />
      <CoveragePanel rows={data.coverage ?? []} />
      {editing ? (
        <CellDialog
          grid={data}
          dayOfWeek={editing.dayOfWeek}
          period={editing.period}
          lesson={editing.lesson}
          onOpenChange={(open) => (open ? undefined : setEditing(null))}
        />
      ) : null}
    </div>
  )
}

function GridCard({
  grid,
  canEdit,
  onCopy,
  onEdit,
}: {
  grid: SectionGrid
  canEdit: boolean
  onCopy?: () => void
  onEdit: (cell: EditingCell) => void
}) {
  const { t } = useTranslation('timetable')
  const hasLessonPeriods = grid.periods.some((p) => !p.isBreak)
  return (
    <Card data-print-document>
      <CardHeader
        title={t('{{section}} timetable', { section: grid.section.name })}
        description={t('{{year}} · {{count}} periods a week', {
          year: grid.academicYear.name,
          count: grid.totalPeriods,
        })}
        icon={<CalendarDays />}
        actions={
          <div className="flex gap-2 print:hidden">
            {canEdit && onCopy ? (
              <Button size="sm" variant="secondary" leftIcon={<Copy />} onClick={onCopy}>
                {t('Copy from…')}
              </Button>
            ) : null}
            <Button size="sm" variant="secondary" leftIcon={<Printer />} onClick={() => window.print()}>
              {t('Print')}
            </Button>
          </div>
        }
      />
      <div className="p-3 sm:p-4">
        {!hasLessonPeriods ? (
          <EmptyState
            icon={<Settings2 />}
            title={t('No periods yet')}
            description={t('Set up the bell schedule before building timetables.')}
            action={
              canEdit ? (
                <Button asChild size="sm" variant="soft">
                  <Link to="/admin/timetable/setup">{t('Set up periods')}</Link>
                </Button>
              ) : undefined
            }
          />
        ) : grid.days.length === 0 ? (
          <EmptyState
            icon={<CalendarDays />}
            title={t('The school has no open days')}
            description={t('Set the opening hours in the school profile.')}
          />
        ) : (
          <WeekGrid
            week={grid}
            view="section"
            caption={t('Weekly timetable of {{section}}', { section: grid.section.name })}
            onCellClick={
              canEdit ? (dayOfWeek, period, lesson) => onEdit({ dayOfWeek, period, lesson }) : undefined
            }
          />
        )}
      </div>
    </Card>
  )
}

export function ClassTimetablePage() {
  const { t } = useTranslation('timetable')
  useDocumentTitle('Class timetables')
  const classes = useQuery(classesQuery())
  const [params, setParams] = useSearchParams()
  const [copying, setCopying] = useState(false)
  const classId = Number(params.get('class')) || undefined
  const sectionId = Number(params.get('section')) || undefined
  const current = classes.data?.find((c) => c.id === classId)
  const section = current?.sections.find((s) => s.id === sectionId)

  const pickClass = (value: string) => {
    const next = classes.data?.find((c) => c.id === Number(value))
    const first = next?.sections[0]
    setParams(next ? { class: String(next.id), ...(first ? { section: String(first.id) } : {}) } : {}, {
      replace: true,
    })
  }
  const pickSection = (value: string) => {
    if (classId)
      setParams(value ? { class: String(classId), section: value } : { class: String(classId) }, {
        replace: true,
      })
  }

  return (
    <>
      <PageHeader
        title={t('Class timetables')}
        description={t('Build each section’s weekly timetable without teacher or room clashes.')}
        breadcrumbs={[{ label: t('Timetable') }, { label: t('Class timetables') }]}
        homeHref="/admin"
      />
      {classes.isPending ? (
        <LoadingState />
      ) : classes.error ? (
        <ErrorState onRetry={() => void classes.refetch()} />
      ) : (
        <>
          <Card className="mb-6 p-4 print:hidden">
            <div className="grid gap-4 sm:grid-cols-2 lg:max-w-2xl">
              <FormField label={t('Class')}>
                <Select
                  options={classes.data.map((c) => ({ value: c.id, label: c.name }))}
                  placeholder={t('Choose a class')}
                  value={classId ?? ''}
                  onChange={(e) => pickClass(e.target.value)}
                />
              </FormField>
              <FormField label={t('Section')}>
                <Select
                  options={(current?.sections ?? []).map((s) => ({
                    value: s.id,
                    label: s.name || t('Whole class'),
                  }))}
                  placeholder={t('Choose a section')}
                  value={section?.id ?? ''}
                  onChange={(e) => pickSection(e.target.value)}
                  disabled={!current}
                />
              </FormField>
            </div>
          </Card>
          {section && current ? (
            <SectionTimetable key={section.id} sectionId={section.id} onCopy={() => setCopying(true)} />
          ) : (
            <Card>
              <EmptyState
                icon={<CalendarDays />}
                title={t('Choose a class and section')}
                description={t('Its weekly timetable and curriculum coverage appear here.')}
              />
            </Card>
          )}
          {copying && section && current ? (
            <CopyFromDialog
              sectionId={section.id}
              sectionName={sectionLabel(current.name, section.name, ' – ')}
              classes={classes.data}
              onOpenChange={(open) => setCopying(open)}
            />
          ) : null}
        </>
      )}
    </>
  )
}
